import { RaceSchema, type Race } from '@runner/shared';
import { apiFetch } from '../../api/fetch';

interface ApiResponse {
  data: unknown[];
  meta?: { nextCursor?: string | null };
}

/**
 * Loads the full set of races from `/api/races`. D1 is the single source of
 * truth — the earlier static JSON fallback (`raceService.loadAllRaces`) was
 * removed because keeping two sources meant drift risk and masked failures
 * (a dead API silently degraded to stale JSON instead of surfacing).
 *
 * Network-level caching belongs to CDN / HTTP headers, not a second data
 * source. Callers of this hook should treat an error as a real error and
 * surface it (see useRaceData + ErrorBoundary).
 *
 * Individual record failures are still skipped (`safeParse`) rather than
 * dropping the whole list — preserves partial availability if D1 has one
 * malformed row.
 */
export async function fetchRaces(): Promise<Race[]> {
  // /api/races currently returns the full catalog regardless of country.
  // When the backend adds a country filter, surface it here as an argument.
  const res = await apiFetch('/api/races?limit=1000');
  if (!res.ok) {
    throw new Error(`Failed to fetch races: ${res.status}`);
  }

  const body = (await res.json()) as ApiResponse;
  if (!Array.isArray(body.data)) {
    throw new Error('Malformed /api/races response: missing data array');
  }

  const races: Race[] = [];
  for (const raw of body.data) {
    const parsed = RaceSchema.safeParse(raw);
    if (parsed.success) {
      races.push(parsed.data);
    } else {
      console.warn('[api/races] Skipping invalid race record:', parsed.error.issues);
    }
  }

  // Stored race.status goes stale, but the server now derives the time-correct
  // phase at every read boundary (race query adapter + recommend/popular/mine),
  // so /api/races already returns normalized status — no second correction here.
  // See docs/adr/0002-promote-race-phase-to-shared.md.
  return races;
}
